import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { Env } from '@/consts/Env';
import { isStaging } from '@/libs/getEnvironment';
import { APIError, refreshTokensSingleFlight } from '@/queries/httpClient';

const SOCKET_URL = isStaging()
  ? Env.NEXT_PUBLIC_STAGE_SOCKET_URL
  : Env.NEXT_PUBLIC_PROD_SOCKET_URL;

const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 30_000;

export interface SocketManagerOptions {
  onSocketChange: (socket: Socket | null) => void;
}

export function createSocketManager({ onSocketChange }: SocketManagerOptions) {
  let isDestroyed = false;
  let reconnectAttempts = 0;
  let activeSocket: Socket | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const disconnectSocket = () => {
    if (!activeSocket) {
      return;
    }

    activeSocket.removeAllListeners();
    activeSocket.disconnect();
    activeSocket = null;
  };

  const resetConnection = () => {
    clearReconnectTimer();
    disconnectSocket();
    onSocketChange(null);
  };

  const connect = (authToken: string) => {
    clearReconnectTimer();
    disconnectSocket();

    if (isDestroyed) {
      return;
    }

    const socket = io(SOCKET_URL, {
      // 'websocket' is tried first; 'polling' is kept as a fallback for
      // networks/proxies that block the WS upgrade handshake.
      transports: ['websocket', 'polling'],
      auth: { token: authToken },
    });

    activeSocket = socket;
    onSocketChange(socket);

    socket.on('connect', () => {
      if (isDestroyed) {
        return;
      }

      reconnectAttempts = 0;
    });

    socket.on('connect_error', (error: Error) => {
      if (isDestroyed || error?.message?.trim() !== 'invalid token') {
        return;
      }

      socket.off('connect_error');
      scheduleReconnect();
    });

    socket.on('disconnect', (reason: string) => {
      // socket.io already auto-retries every other disconnect reason itself.
      if (isDestroyed || reason !== 'io server disconnect') {
        return;
      }

      scheduleReconnect();
    });
  };

  // Retries indefinitely with capped exponential backoff instead of giving
  // up after a fixed attempt count — a dead session gets cleaned up via
  // destroy() when userSession flips, not by this loop stopping itself.
  function scheduleReconnect() {
    if (isDestroyed) {
      return;
    }

    clearReconnectTimer();
    const delay = Math.min(INITIAL_RECONNECT_DELAY_MS * 2 ** reconnectAttempts, MAX_RECONNECT_DELAY_MS);
    reconnectAttempts += 1;

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      void refreshTokenAndReconnect();
    }, delay);
  }

  async function refreshTokenAndReconnect() {
    if (isDestroyed) {
      return;
    }

    try {
      const { accessToken } = await refreshTokensSingleFlight();

      if (isDestroyed || !accessToken) {
        return;
      }

      connect(accessToken);
    } catch (error) {
      if (isDestroyed) {
        return;
      }

      // A rejected (non-network) refresh means the token is dead — stop instead of retrying forever.
      if (error instanceof APIError && error.status !== 0) {
        resetConnection();
        return;
      }

      scheduleReconnect();
    }
  }

  // Lets callers (e.g. tab visibility/online listeners) nudge an immediate
  // reconnect attempt instead of waiting out socket.io's own backoff timer.
  const reconnectNow = () => {
    if (isDestroyed || !activeSocket || activeSocket.connected) {
      return;
    }

    activeSocket.connect();
  };

  const destroy = () => {
    isDestroyed = true;
    resetConnection();
  };

  return { connect, destroy, reconnectNow };
}
